import React, { useState, useEffect } from 'react';
import Loading from '../../component/Loading/Loading';
import AlertMsg from '../../component/AlertMsg/AlertMsg';
import { ArrowUpDown, Shield, Save, X } from "lucide-react";
import useAxiosInstance from '../../services/useAxiosInstance';

const RolePageAllow = () => {
    const axiosInstance = useAxiosInstance();

    const [loading, setLoading] = useState(true);
    const [rolePages, setRolePages] = useState([]);
    const [roleIds, setRoleIds] = useState(1000);
    const [showFormModal, setShowFormModal] = useState(false);
    const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState([]);
    const [roles, setRoles] = useState([]);
    const [pages, setPages] = useState([]);
    const [searchName, setSearchName] = useState("");
    const [sortOrder, setSortOrder] = useState('asc');

    const handleCancelBtn = () => {
        setIsEditing(false);
    };

    const handleEditBtn = () => {
        setIsEditing(true);
    };

    useEffect(() => {
        const fetchRoles = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/api/public/role/getRoles`);
                setRoles(response.data);
            } catch (error) {
                console.error('Error fetching roles', error);
            } finally {
                setLoading(false);
            }
        };

        const fetchPages = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/api/public/page/getAll`);
                setPages(response.data);
            } catch (error) {
                console.error('Error fetching pages', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRoles();
        fetchPages();
    }, []);

    useEffect(() => {
        if (roleIds) {
            fetchRolePageData();
        }
    }, [roleIds]);

    const fetchRolePageData = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/api/public/rolepage/getByRoleId/{roleId}`, {
                params: { roleId: roleIds }
            });
            setRolePages(response.data);
        } catch (error) {
            console.error('Error fetching role data:', error);
        } finally {
            setLoading(false);
        }
    };

    const openFormModal = () => {
        setShowFormModal(true);
    };

    const handleSearchChange = (event) => {
        setSearchName(event.target.value);
    };

    const handleCheckboxChange = (event, index) => {
        const { name, checked } = event.target;
        setRolePages((prevRolePages) => {
            const updatedRolePages = [...prevRolePages];
            updatedRolePages[index] = {
                ...updatedRolePages[index],
                [name]: checked
            };
            return updatedRolePages;
        });

        setFormData(prevFormData => {
            const updatedFormData = {
                ...prevFormData,
                [name]: checked,
                rolepageid: rolePages[index].rolepageid
            };
            console.log("Updated formData:", updatedFormData);
            return updatedFormData;
        });
    };

    const handleRowClick = (rolepageid) => {
        setFormData(prevFormData => ({
            ...prevFormData,
            rolepageid
        }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            for (const rolePage of rolePages) {
                const { rolepageid, ...dataToUpdate } = rolePage;
                const updateFormatData = {
                    access: dataToUpdate.access,
                    create: dataToUpdate.create,
                    delete: dataToUpdate.delete,
                    pageId: dataToUpdate.pageId,
                    read: dataToUpdate.read,
                    roleId: dataToUpdate.roleId,
                    rolePageId: dataToUpdate.rolePageId,
                    update: dataToUpdate.update
                };
                console.log(updateFormatData);
                await axiosInstance.put(`/api/public/rolepage/update/${updateFormatData.rolePageId}`, updateFormatData);
            }

            fetchRolePageData();
            setAlert({ visible: true, message: 'Role pages updated successfully!', type: 'success' });
        } catch (error) {
            setAlert({ visible: true, message: 'Error updating role pages: Please try again.', type: 'error' });
            console.error('Error saving role pages:', error);
        } finally {
            setLoading(false);
            setIsEditing(false);
        }
    };

    const handleRowCheckboxChange = (event, index) => {
        const { checked } = event.target;
        setRolePages((prevRolePages) => {
            const updatedRolePages = [...prevRolePages];
            updatedRolePages[index] = {
                ...updatedRolePages[index],
                read: checked,
                create: checked,
                update: checked,
                delete: checked,
                access: checked,
            };
            return updatedRolePages;
        });
    };

    const handleSort = () => {
        const sortedRolePages = [...rolePages].sort((a, b) => {
            const pageNameA = a.pageName.toLowerCase();
            const pageNameB = b.pageName.toLowerCase();

            if (sortOrder === 'asc') {
                return pageNameA.localeCompare(pageNameB);
            } else {
                return pageNameB.localeCompare(pageNameA);
            }
        });

        setRolePages(sortedRolePages);
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    };

    const filteredData = rolePages.filter((request) =>
        request.pageName.toLowerCase().includes(searchName.toLowerCase())
    );

    return (
        <div className="bg-gray-50 min-h-screen md:p-4">
            {loading && <Loading />}
            {!loading && (
                <div className="max-w-7xl mx-auto">
                    {alert.visible && (
                        <AlertMsg
                            message={alert.message}
                            type={alert.type}
                            onClose={() => setAlert({ ...alert, visible: false })}
                        />
                    )}

                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">Role Page Permissions</h1>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
                        <div className="p-4 flex flex-col md:flex-row justify-between items-center border-b border-gray-200">
                            <div className="flex items-center justify-start w-full md:w-auto mb-4 md:mb-0">
                                <div className="relative w-full md:w-auto">
                                    <div className="flex items-center">
                                        <Shield className="h-5 w-5 text-gray-400 mr-2" />
                                        <select
                                            id="roleids"
                                            value={roleIds}
                                            onChange={(e) => setRoleIds(e.target.value)}
                                            className="w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                        >
                                            <option value="">Select Role</option>
                                            {roles.map((role) => (
                                                <option key={role.roleid} value={role.roleid}>
                                                    {role.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className='flex items-center justify-end w-full md:w-auto'>
                                {isEditing ? (
                                    <div className="flex gap-2 w-full md:w-auto">
                                        <button
                                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center gap-1 flex-1"
                                            onClick={handleCancelBtn}
                                        >
                                            <X size={18} />
                                            <span>Cancel</span>
                                        </button>
                                        <button
                                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-1 flex-1"
                                            onClick={handleEditSubmit}
                                        >
                                            <Save size={18} />
                                            <span>Save</span>
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        className="w-full md:w-fit px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                        onClick={handleEditBtn}
                                    >
                                        Edit Permissions
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-blue-400">
                                    <tr>
                                        <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider">
                                            Select
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">
                                            <div className="flex items-center flex-row  whitespace-nowrap">
                                                <div>Page Name</div>
                                                <button onClick={handleSort} className="ml-1 text-white flex">
                                                    <ArrowUpDown size={16} />
                                                </button>
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider">
                                            Access
                                        </th>
                                        <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider">
                                            Read
                                        </th>
                                        <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider">
                                            Create
                                        </th>
                                        <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider">
                                            Update
                                        </th>
                                        <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider">
                                            Delete
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {rolePages.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                                                No Data Found
                                            </td>
                                        </tr>
                                    ) : (
                                        rolePages.map((contact, index) => (
                                            <tr
                                                key={index}
                                                onClick={() => handleRowClick(contact.rolepageid)}
                                                className={`cursor-pointer hover:bg-gray-50 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex justify-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={contact.access && contact.read && contact.create && contact.update && contact.delete}
                                                            onChange={(event) => handleRowCheckboxChange(event, index)}
                                                            disabled={!isEditing}
                                                            className="rounded text-blue-500 focus:ring-blue-500 cursor-pointer"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {contact.pageName}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex justify-center">
                                                        <input
                                                            type="checkbox"
                                                            name="access"
                                                            checked={contact.access}
                                                            onChange={(event) => handleCheckboxChange(event, index)}
                                                            disabled={!isEditing}
                                                            className="rounded text-blue-500 focus:ring-blue-500 cursor-pointer"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex justify-center">
                                                        <input
                                                            type="checkbox"
                                                            name="read"
                                                            checked={contact.read}
                                                            onChange={(event) => handleCheckboxChange(event, index)}
                                                            disabled={!isEditing}
                                                            className="rounded text-blue-500 focus:ring-blue-500 cursor-pointer"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex justify-center">
                                                        <input
                                                            type="checkbox"
                                                            name="create"
                                                            checked={contact.create}
                                                            onChange={(event) => handleCheckboxChange(event, index)}
                                                            disabled={!isEditing}
                                                            className="rounded text-blue-500 focus:ring-blue-500 cursor-pointer"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex justify-center">
                                                        <input
                                                            type="checkbox"
                                                            name="update"
                                                            checked={contact.update}
                                                            onChange={(event) => handleCheckboxChange(event, index)}
                                                            disabled={!isEditing}
                                                            className="rounded text-blue-500 focus:ring-blue-500 cursor-pointer"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex justify-center">
                                                        <input
                                                            type="checkbox"
                                                            name="delete"
                                                            checked={contact.delete}
                                                            onChange={(event) => handleCheckboxChange(event, index)}
                                                            disabled={!isEditing}
                                                            className="rounded text-blue-500 focus:ring-blue-500 cursor-pointer"
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RolePageAllow;